/**
 * ログインの上限（本部管理者のみ・#336）。
 *
 * 電話番号＋認証番号でのログインには、SMS を大量に送らせる・認証番号を当てようとする攻撃への
 * 歯止めとして上限があります。ここでは、
 *  - いまの上限の数（いつもの数と、いま効いている数）
 *  - **導入の日など一斉に入っていただく日の、一時的な引き上げ**（何倍に・何時間。時間がたつと自動で戻る）
 *  - この7日間に上限に当たった記録
 *  - **間違いの数の数え直し**（会社全体／1人の電話番号・#368）。わざと間違えられて止められたとき、
 *    倍率を上げずに戻せる。電話番号ごとの上限は引き上げでは変わらないので、戻す手段はこれだけ
 * を扱います。引き上げ・取りやめは確認の窓をはさみ、結果を帯で出し、操作履歴に残ります（決定22）。
 *
 * いつもの数そのものは、要件定義で人数を伺ってサーバの設定で決めます（この画面では変えません）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../api/context.js';
import { loadFailure, nextStepFor, type LoadFailure } from '../api/load-failure.js';
import type { LoginLimitNumbers, LoginLimitResetResult, LoginLimitStatus } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { formatDateTime, formatTableDateTime } from '../format/datetime.js';
import { formatPhone } from '../format/phone.js';
import { withReason } from '../format/sentence.js';
import { ROLE_REQUIREMENTS, useRoleWording } from '../auth/role-wording.js';

type LimitKey = Exclude<keyof LoginLimitNumbers, 'source_requests_per_window'>;

/** 表に並べる上限（言葉・単位・引き上げで変わるか）。 */
const ROWS: { key: LimitKey; label: string; unit: string; raisable: boolean }[] = [
  {
    key: 'org_sends_per_hour',
    label: '会社全体で認証番号を送れる数（1時間）',
    unit: '通',
    raisable: true,
  },
  {
    key: 'org_sends_per_day',
    label: '会社全体で認証番号を送れる数（24時間）',
    unit: '通',
    raisable: true,
  },
  {
    key: 'source_sends_per_day',
    label: '1か所（会社の Wi‑Fi など）から認証番号を送れる数（24時間）',
    unit: '通',
    raisable: true,
  },
  {
    key: 'phone_sends_per_day',
    label: '1人の電話番号へ認証番号を送れる数（24時間）',
    unit: '通',
    raisable: false,
  },
  {
    key: 'phone_failures_per_day',
    label: '1人の電話番号で認証番号を間違えられる回数（24時間）',
    unit: '回',
    raisable: false,
  },
  {
    key: 'source_failures_per_day',
    label: '1か所から認証番号を間違えられる回数（24時間）',
    unit: '回',
    raisable: true,
  },
  {
    key: 'org_failures_per_day',
    label: '会社全体で認証番号を間違えられる回数（24時間）',
    unit: '回',
    raisable: true,
  },
];

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
}

/** 短い枠の長さの言い方（60秒 → 「1分」・issue #443）。 */
function windowLabel(seconds: number): string {
  return seconds % 60 === 0 ? `${seconds / 60}分` : `${seconds}秒`;
}

/** 短い枠の回数の見せ方（0 は数えていない＝上限なし）。 */
function burstValue(value: number): string {
  return value > 0 ? `${value.toLocaleString('ja-JP')}回` : '上限なし';
}

/**
 * 「引き上げをやめる」の確認の窓の言葉（#475 W-16）。
 * 数えているのは「この24時間」なので、引き上げている間に同じ Wi‑Fi などから、いつもの上限より多く
 * 送った・間違えたときは、戻したとたんにその場所から入れなくなる。押す前にそのことを伝える（決定22）。
 */
const CANCEL_RAISE_MESSAGE =
  'ログインの上限を、いつもの数に戻します。この24時間に、同じ場所（会社の Wi‑Fi など）から、いつもの上限より多く認証番号を送った・間違えたときは、戻したすぐあとから、その場所では最長24時間、新しくログインできなくなります。まだログインする方が残っているときは、みなさんが入り終えてから戻してください。';

/** 何時間後の時刻（確認の窓で「いつまで」をお見せする）。 */
function hoursLater(hours: number): string {
  return formatDateTime(new Date(Date.now() + hours * 60 * 60 * 1000).toISOString());
}

export function LoginLimitsPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const isHq = profile?.grant.scope === 'org';
  const [status, setStatus] = useState<LoginLimitStatus | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<Banner | null>(null);
  /** 数え直しの結果は、押したカードの中に出す（ページの上の帯だと、押した場所から見えないため） */
  const [resetResult, setResetResult] = useState<Banner | null>(null);
  const [resetScope, setResetScope] = useState<'org' | 'phone'>('org');
  const [factor, setFactor] = useState(3);
  const [hours, setHours] = useState(6);
  const [confirming, setConfirming] = useState<
    'raise' | 'cancel' | 'reset_org' | 'reset_phone' | null
  >(null);
  /** 数え直す電話番号（入れた文字のまま。サーバが数字にそろえる） */
  const [resetPhone, setResetPhone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isHq) return;
    let cancelled = false;
    setError(null);
    api
      .get<LoginLimitStatus>('/admin/login-limits')
      .then((loaded) => {
        if (!cancelled) setStatus(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setStatus(null);
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey, isHq]);

  if (!profile) return null;
  if (!isHq) {
    return (
      <article>
        <PageHeader title="ログインの上限" />
        <p className="admin-empty">
          {/* 役職の呼び名はその会社のもの（issue #480） */}
          {wording.only(ROLE_REQUIREMENTS.assignRoles, 'この画面をお使いいただけるのは')}
        </p>
      </article>
    );
  }

  async function run(kind: 'raise' | 'cancel'): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      if (kind === 'raise') {
        const next = await api.post<LoginLimitStatus>('/admin/login-limits/raise', {
          body: { factor, hours },
        });
        setStatus(next);
        setResult({
          tone: 'success',
          message: `ログインの上限を${factor}倍に引き上げました（${formatDateTime(
            next.raise?.ends_at ?? null,
          )}まで）。`,
          nextStep:
            '時間がたつと、自動でいつもの上限に戻ります。早めに戻すときは「引き上げをやめる」を押してください。',
        });
      } else {
        const next = await api.post<LoginLimitStatus>('/admin/login-limits/cancel');
        setStatus(next);
        setResult({ tone: 'success', message: 'ログインの上限を、いつもの数に戻しました。' });
      }
    } catch (caught) {
      const failure = loadFailure(caught);
      setResult({
        tone: 'error',
        message: withReason(
          kind === 'raise'
            ? 'ログインの上限を引き上げられませんでした。'
            : 'ログインの上限を戻せませんでした。',
          failure.message,
        ),
        nextStep: nextStepFor(failure.status, failure.retryable).nextStep,
      });
      setReloadKey((n) => n + 1);
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }

  async function resetCounts(scope: 'org' | 'phone'): Promise<void> {
    setBusy(true);
    setResetResult(null);
    setResult(null);
    try {
      const done = await api.post<LoginLimitResetResult>('/admin/login-limits/reset', {
        body: scope === 'org' ? { scope } : { scope, phone: resetPhone },
      });
      setStatus(done.status);
      const { reset } = done;
      setResetScope(reset.scope);
      if (reset.scope === 'org') {
        setResetResult(
          reset.failures > 0
            ? {
                tone: 'success',
                message: `会社全体の認証番号の間違い ${reset.failures}回分を数え直しました。いまからログインできます。`,
                nextStep:
                  '同じことが続くときは、わざと間違えられている可能性があります。サーバを預かる担当へご相談ください。',
              }
            : {
                // 数え直すものが無かった＝上限で止まってはいなかった（issue #390）
                tone: 'info',
                message:
                  '数え直すものはありませんでした（会社全体の認証番号の間違いは、この24時間に0回です）。',
                nextStep:
                  '会社全体の上限で止まってはいませんでした。ログインできない方がいるときは、1人の電話番号の数え直しや、メンバーの電話番号をお確かめください。',
              },
        );
      } else {
        setResetPhone('');
        const last4 = reset.phone_last4 ?? '';
        setResetResult(
          reset.failures + reset.sends > 0
            ? {
                tone: 'success',
                message: `電話番号（下4桁 ${last4}）の、間違い ${reset.failures}回・送った数 ${reset.sends}通を数え直しました。`,
                // 同じ番号へ直前に送っていると、送り直しは1分ほど待つことがある（issue #390）
                nextStep:
                  'その方は、いまから認証番号を送り直せます（前回の送信から1分ほど待つ場合があります）。',
              }
            : {
                tone: 'info',
                message: `数え直すものはありませんでした（電話番号 下4桁 ${last4} は、この24時間に数えていた回数がありません）。`,
                nextStep:
                  'この番号は上限で止まってはいませんでした。メンバーの電話番号が合っているか、無効にされていないかをお確かめください。',
              },
        );
      }
    } catch (caught) {
      const failure = loadFailure(caught);
      setResetScope(scope);
      setResetResult({
        tone: 'error',
        message: withReason('数え直せませんでした。', failure.message),
        nextStep: nextStepFor(failure.status, failure.retryable).nextStep,
      });
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }

  const raise = status?.raise ?? null;
  const factors = status?.factors ?? [2, 3, 5, 10];
  const hourChoices = status?.hours ?? [3, 6, 12, 24];

  return (
    <article>
      <PageHeader
        title="ログインの上限"
        description="電話番号でのログインで、認証番号を送れる数と、間違えられる回数の上限です。SMS を大量に送らせたり、認証番号を当てようとしたりする攻撃への歯止めです。"
      />

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !status ? <LoadingBlock label="上限を読み込んでいます…" /> : null}

      {status ? (
        <>
          <Card title="いまの様子">
            {raise ? (
              <ResultBanner
                tone="info"
                message={`いま、上限を${raise.factor}倍に引き上げています（${formatDateTime(
                  raise.ends_at,
                )}まで${raise.raised_by_name ? `・${raise.raised_by_name}さんが引き上げました` : ''}）。`}
                nextStep="時間がたつと、自動でいつもの上限に戻ります。"
                action={
                  <Button
                    variant="secondary"
                    compact
                    disabled={busy}
                    onClick={() => setConfirming('cancel')}
                  >
                    引き上げをやめる
                  </Button>
                }
              />
            ) : (
              <p>いつもの上限で動いています。</p>
            )}
            <DataTable label="ログインの上限の数">
              <thead>
                <tr>
                  <th scope="col">上限</th>
                  <th scope="col">いつもの数</th>
                  <th scope="col">いまの数</th>
                </tr>
              </thead>
              <tbody>
                {/* ログイン前の窓口の短い枠（同じ Wi‑Fi から一斉に押すと当たる・issue #443） */}
                {status.base.source_requests_per_window !== undefined &&
                status.limits.source_requests_per_window !== undefined ? (
                  <tr>
                    <th scope="row" className="ui-cell-wrap">
                      {`1か所（会社の Wi‑Fi など）から、認証番号を送る操作・入れる操作それぞれの回数（${windowLabel(
                        status.source_requests_window_seconds ?? 60,
                      )}）`}
                    </th>
                    <td>{burstValue(status.base.source_requests_per_window)}</td>
                    <td>{burstValue(status.limits.source_requests_per_window)}</td>
                  </tr>
                ) : null}
                {ROWS.map((row) => (
                  <tr key={row.key}>
                    <th scope="row" className="ui-cell-wrap">
                      {row.label}
                      {!row.raisable ? (
                        <small className="admin-choice-detail">引き上げても変わりません</small>
                      ) : null}
                    </th>
                    <td>
                      {status.base[row.key].toLocaleString('ja-JP')}
                      {row.unit}
                    </td>
                    <td>
                      {status.limits[row.key].toLocaleString('ja-JP')}
                      {row.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <p className="admin-note">
              上限に当たると、その間は新しくログインできない方が出ます（すでにログインしている方は、そのまま使えます）。いつもの数は、会社の人数に合わせてサーバの設定で決めます。
            </p>
          </Card>

          <FormCard
            title="一時的に引き上げる（導入の日など）"
            description="全員が一斉にログインする日（導入の初日の朝礼など）は、同じ会社の Wi‑Fi から続けて送られるため、上限に当たることがあります。始める前に引き上げてください。24時間の上限と、1分あたりの回数の両方が広がります。時間がたつと、自動でいつもの上限に戻ります。"
            submitLabel="引き上げる"
            busy={busy && confirming === 'raise'}
            onSubmit={() => setConfirming('raise')}
          >
            <div className="admin-limit-form">
              <label className="ui-field" htmlFor="limit-factor">
                <span>何倍に</span>
                <select
                  id="limit-factor"
                  value={factor}
                  onChange={(event) => setFactor(Number(event.target.value))}
                >
                  {factors.map((value) => (
                    <option key={value} value={value}>
                      {value}倍
                    </option>
                  ))}
                </select>
              </label>
              <label className="ui-field" htmlFor="limit-hours">
                <span>何時間</span>
                <select
                  id="limit-hours"
                  value={hours}
                  onChange={(event) => setHours(Number(event.target.value))}
                >
                  {hourChoices.map((value) => (
                    <option key={value} value={value}>
                      {value}時間
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </FormCard>

          <Card
            title="間違いの数を数え直す"
            description="認証番号をわざと間違えられて、ログインできなくなったときに使います。数え直すと、この24時間の間違いの数が0に戻ります（上限の数そのものは変わりません）。"
          >
            <p>
              この24時間の、会社全体の認証番号の間違いは
              <strong>{(status.org_failures_today ?? 0).toLocaleString('ja-JP')}回</strong>
              です（上限 {status.limits.org_failures_per_day.toLocaleString('ja-JP')}回）。
            </p>
            <div className="ui-form-actions">
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => setConfirming('reset_org')}
              >
                会社全体の間違いを数え直す
              </Button>
            </div>
            {resetResult && resetScope === 'org' ? <ResultBanner {...resetResult} /> : null}
          </Card>

          <FormCard
            title="1人の電話番号を数え直す"
            description="「この電話番号では、今日はこれ以上ログインをお試しいただけません」と出て入れない方の、間違えた回数と送った数を0に戻します。"
            submitLabel="この番号を数え直す"
            busy={busy && confirming === 'reset_phone'}
            footer={
              resetResult && resetScope === 'phone' ? <ResultBanner {...resetResult} /> : undefined
            }
            onSubmit={() => {
              if (resetPhone.trim() === '') {
                setResetScope('phone');
                setResetResult({
                  tone: 'error',
                  message: '数え直す電話番号を入れてください。',
                  nextStep: 'ログインできない方の電話番号を入れてから、もう一度押してください。',
                });
                return;
              }
              setConfirming('reset_phone');
            }}
          >
            <label className="ui-field" htmlFor="limit-reset-phone">
              <span>電話番号</span>
              <input
                id="limit-reset-phone"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                placeholder="例: 09012345678"
                value={resetPhone}
                onChange={(event) => setResetPhone(event.target.value)}
              />
            </label>
          </FormCard>

          <Card
            title="この7日間に上限に当たった記録"
            description="心当たりが無いときは、大量に送らせようとされている可能性があります。"
          >
            {status.reached.length === 0 ? (
              <EmptyBlock label="この7日間に上限に当たったことはありません。" />
            ) : (
              <DataTable label="上限に当たった記録">
                <thead>
                  <tr>
                    <th scope="col">日時</th>
                    <th scope="col">当たった上限</th>
                  </tr>
                </thead>
                <tbody>
                  {status.reached.map((item, index) => (
                    <tr key={`${item.at}-${index}`}>
                      <td className="ui-cell-soft">{formatTableDateTime(item.at)}</td>
                      <td className="ui-cell-wrap">
                        {item.label}
                        {item.limit !== null
                          ? `（上限 ${item.limit.toLocaleString('ja-JP')}）`
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Card>
        </>
      ) : null}

      <ConfirmDialog
        open={confirming === 'raise'}
        title="ログインの上限を引き上げます"
        message={`会社全体と、1か所から送れる数・間違えられる回数・1分あたりの回数の上限を${factor}倍にします（${hoursLater(
          hours,
        )}ごろまで）。1人の電話番号ごとの上限は変わりません。`}
        reversibility="いつでも「引き上げをやめる」で、いつもの上限に戻せます。"
        reversible
        tone="primary"
        confirmLabel="引き上げる"
        busy={busy && confirming === 'raise'}
        onConfirm={() => void run('raise')}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'reset_org'}
        title="会社全体の間違いを数え直します"
        message="この24時間の、会社全体の認証番号の間違いの数を0に戻します。止まっていたログインが、すぐにできるようになります。"
        reversibility="数え直したことは操作履歴に残ります。数え直した回数は元に戻せません。"
        tone="primary"
        confirmLabel="数え直す"
        busy={busy && confirming === 'reset_org'}
        onConfirm={() => void resetCounts('org')}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'reset_phone'}
        title="この電話番号を数え直します"
        message={`電話番号 ${formatPhone(resetPhone.trim())} の、この24時間の間違えた回数と送った数を0に戻します。`}
        reversibility="数え直したことは操作履歴に残ります。数え直した回数は元に戻せません。"
        tone="primary"
        confirmLabel="数え直す"
        busy={busy && confirming === 'reset_phone'}
        onConfirm={() => void resetCounts('phone')}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'cancel'}
        title="引き上げをやめます"
        message={CANCEL_RAISE_MESSAGE}
        reversibility="戻したあとでも、もう一度引き上げられます。"
        reversible
        tone="primary"
        confirmLabel="いつもの上限に戻す"
        busy={busy && confirming === 'cancel'}
        onConfirm={() => void run('cancel')}
        onCancel={() => setConfirming(null)}
      />
    </article>
  );
}
